import React from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { ChevronDown, LogOut, Menu, KeyRound } from "lucide-react";
import { ThemeToggle } from "@/shared/components/ui/themeToggle";
import { useLogout } from "@/hooks/useLogout";
import { useState } from "react";
import { ChangePasswordDialog } from "@retro-ia/shared/password";

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header = React.memo(function Header({ onMenuClick }: HeaderProps) {
  const { user } = useCurrentUser();
  const { mutate: logout, isPending } = useLogout();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  return (
    <header className="w-full flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 md:py-4 bg-(--card-background)">
      <div className="flex items-center gap-3">
        {/* Botón hamburguesa visible solo en móvil */}
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        <div className="flex flex-col items-end gap-0.5 sm:gap-1">
          <h1 className="font-bold text-xl sm:text-2xl md:text-3xl bg-linear-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            RETRO IA
          </h1>
          <p className="text-[0.6rem] sm:text-xs text-muted-foreground">
            by &lt;ALVESC/&gt;
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 sm:gap-6 md:gap-10">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 sm:gap-3 focus:outline-none">
            <Avatar className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10">
              <AvatarFallback className="text-xs sm:text-sm">
                {user?.nombre?.[0]}
                {user?.apellidoPaterno?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-(--foreground)">
                {user?.nombre} {user?.apellidoPaterno}
              </p>
              <p className="text-xs text-muted-foreground">{user?.correo}</p>
            </div>
            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="w-48 sm:w-56 bg-[var(--background)] border border-border shadow-md"
          >
            <DropdownMenuItem
              onClick={() => setChangePasswordOpen(true)}
              className="cursor-pointer hover:bg-[var(--muted)] text-sm"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Cambiar contraseña
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout()}
              disabled={isPending}
              className="cursor-pointer hover:bg-[var(--muted)] text-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isPending ? "Cerrando..." : "Cerrar sesión"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ThemeToggle />
      </div>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </header>
  );
});
